import { useEffect, useRef, useState } from "react";
import { Mic, Send, Square, Volume2, VolumeX } from "lucide-react";
import { api } from "../../lib/api";
import { apiError } from "../../lib/auth";
import { GuardOrb3D, LEVEL_COLOR } from "./GuardOrb3D";
import { useGuardVoice } from "./useGuardVoice";

const SUGGEST = ["Is the platform healthy right now?", "Explain the open errors and how to fix them", "Any fraud or attacks today?", "Which buyers look risky?"];

export const GuardConsole = ({ level = "secure", session = "guard", big = false, testid = "guard-console" }) => {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [voiceOn, setVoiceOn] = useState(true);
  const levelRef = useRef(0);
  const end = useRef(null);
  const v = useGuardVoice(levelRef);
  useEffect(() => { api.get(`/guard/chat?session_id=${session}`).then((r) => setMsgs(r.data)).catch(() => {}); }, [session]);
  useEffect(() => { end.current?.scrollIntoView({ block: "nearest" }); }, [msgs, busy]);

  const send = async (m) => {
    const q = (m ?? text).trim();
    if (!q || busy) return;
    setText("");
    setMsgs((x) => [...x, { role: "user", text: q }]);
    setBusy(true);
    try {
      const { data } = await api.post("/guard/chat", { message: q, session_id: session });
      setMsgs((x) => [...x, { role: "guard", text: data.reply }]);
      if (voiceOn) v.speak(data.reply);
    } catch (e) { setMsgs((x) => [...x, { role: "guard", text: `I couldn't answer: ${apiError(e)}` }]); }
    finally { setBusy(false); }
  };
  const mic = async () => {
    if (v.listening) { const said = await v.stop(); if (said) send(said); return; }
    v.hush();
    try { await v.start(); } catch (_) { setMsgs((x) => [...x, { role: "guard", text: "I need microphone permission to hear you." }]); }
  };
  const mode = v.listening ? "listening" : busy ? "thinking" : v.speaking ? "speaking" : "idle";
  const status = { listening: "LISTENING… TAP TO SEND", thinking: "ANALYZING", speaking: "RESPONDING", idle: "STANDING BY" }[mode];

  return (
    <div className={`flex flex-col ${big ? "lg:flex-row gap-6" : "gap-3"}`} data-testid={testid}>
      <div className={`flex flex-col items-center ${big ? "lg:w-80 shrink-0" : ""}`}>
        <button onClick={mic} className="relative" title="Tap to talk" data-testid={`${testid}-orb-btn`}>
          <GuardOrb3D level={level} mode={mode} levelRef={levelRef} className={big ? "w-72 h-72" : "w-40 h-40"} testid={`${testid}-orb`} />
        </button>
        <p className="font-mono text-[10px] tracking-[0.25em] mt-1" style={{ color: LEVEL_COLOR[level] }} data-testid={`${testid}-mode`}>{status}</p>
      </div>
      <div className="flex-1 min-w-0 flex flex-col">
        <div className={`overflow-y-auto space-y-3 pr-1 ${big ? "h-80" : "h-56"}`} data-testid={`${testid}-log`}>
          {!msgs.length && <div className="flex flex-wrap gap-2">{SUGGEST.map((s) => (
            <button key={s} onClick={() => send(s)} className="text-xs border border-white/10 px-3 py-1.5 text-white/70 hover:border-[#00E5FF] hover:text-[#00E5FF] transition-colors" data-testid={`${testid}-suggest-${SUGGEST.indexOf(s)}`}>{s}</button>))}</div>}
          {msgs.map((m, i) => (
            <div key={i} className={`text-sm max-w-[92%] ${m.role === "user" ? "ml-auto text-right text-white/80" : "text-white"}`} data-testid={`${testid}-msg-${i}`}>
              <p className="font-mono text-[9px] tracking-[0.2em] text-white/35 mb-0.5">{m.role === "user" ? "YOU" : "AEGIS GUARD"}</p>
              <p className={`inline-block px-3 py-2 border ${m.role === "user" ? "border-white/10 bg-white/[0.03]" : "border-[#00E5FF]/25 bg-[#00E5FF]/[0.04]"}`}>{m.text}</p>
            </div>
          ))}
          {busy && <p className="font-mono text-[10px] text-[#00E5FF] animate-pulse" data-testid={`${testid}-thinking`}>GUARD IS ANALYZING LIVE DATA…</p>}
          <div ref={end} />
        </div>
        <div className="flex items-center gap-2 mt-3">
          <button onClick={mic} className={`p-2.5 border transition-colors ${v.listening ? "border-red-400 text-red-300 animate-pulse" : "border-white/15 text-white/70 hover:text-[#00E5FF] hover:border-[#00E5FF]"}`} data-testid={`${testid}-mic`}>
            {v.listening ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </button>
          <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask Guard about errors, threats, fraud…"
            className="flex-1 bg-black/40 border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:border-[#00E5FF]" data-testid={`${testid}-input`} />
          <button onClick={() => send()} disabled={busy || !text.trim()} className="p-2.5 border border-[#00E5FF]/60 text-[#00E5FF] disabled:opacity-30 hover:bg-[#00E5FF]/10 transition-colors" data-testid={`${testid}-send`}><Send className="w-4 h-4" /></button>
          <button onClick={() => { setVoiceOn(!voiceOn); v.hush(); }} title="Voice replies" className="p-2.5 text-white/50 hover:text-white" data-testid={`${testid}-voice-toggle`}>
            {voiceOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
};
